import { computed, defineComponent, onMounted, onUnmounted, ref, } from 'vue';
import heroImage from '@/assets/hero-2.png';
// Greeting reads from the user's *browser* language (not the navbar locale)
// — it stays personal to the visitor regardless of which UI language they pick.
// Most are 3–6 chars so the giant hero word keeps a roughly consistent rendered width.
const GREETING_BY_LANG = {
    // Slavic
    pl: 'WITAJ',
    cs: 'AHOJ',
    sk: 'AHOJ',
    uk: 'PRYVT',
    ru: 'PRYVT',
    bg: 'ZDRAVO',
    hr: 'BOK',
    sr: 'ZDRAVO',
    sl: 'ZIVJO',
    mk: 'ZDRAVO',
    // Germanic
    en: 'HELLO',
    de: 'HALLO',
    nl: 'HALLO',
    af: 'HALLO',
    da: 'HEJ',
    sv: 'HEJ',
    no: 'HEI',
    nb: 'HEI',
    nn: 'HEI',
    is: 'HALLO',
    // Romance
    fr: 'SALUT',
    it: 'CIAO',
    es: 'HOLA',
    pt: 'OLA',
    ca: 'HOLA',
    gl: 'OLA',
    ro: 'SALUT',
    // Finno-Ugric / Baltic
    fi: 'HEI',
    et: 'TERE',
    hu: 'SZIA',
    lv: 'SVEIKS',
    lt: 'LABAS',
    // Other European
    el: 'GEIA',
    tr: 'SELAM',
    sq: 'TUNG',
    eu: 'KAIXO',
    // Asian
    zh: 'NIHAO',
    ja: 'YAHHO',
    ko: 'YOBO',
    vi: 'XINCHAO',
    th: 'SAWAT',
    id: 'HALO',
    ms: 'HALO',
    hi: 'NAMAS',
    // Middle East
    ar: 'SALAM',
    fa: 'SALAM',
    he: 'SHALOM',
};
const pickGreeting = () => {
    if (typeof window === 'undefined')
        return 'HELLO';
    const langs = [];
    if (window.navigator.languages?.length)
        langs.push(...window.navigator.languages);
    if (window.navigator.language)
        langs.push(window.navigator.language);
    for (const raw of langs) {
        const code = raw.slice(0, 2).toLowerCase();
        if (GREETING_BY_LANG[code])
            return GREETING_BY_LANG[code];
    }
    return 'HELLO';
};
export default defineComponent({
    name: 'Hero',
    setup() {
        const greeting = ref(pickGreeting());
        const scrollY = ref(0);
        const onScroll = () => {
            scrollY.value = window.scrollY;
        };
        onMounted(() => {
            window.addEventListener('scroll', onScroll, { passive: true });
            onScroll();
        });
        onUnmounted(() => {
            window.removeEventListener('scroll', onScroll);
        });
        // Parallax factors: background moves slower, foreground moves faster.
        const bgTextStyle = computed(() => {
            // Pad letter-spacing up to the widest word, but cap it so short words
            // like CIAO don't end up with huge gaps between letters.
            const BASE_CHAR_EM = 0.55;
            const TARGET_CHARS = 5;
            const MAX_SPACING_EM = 0.05;
            const len = greeting.value.length;
            const gaps = Math.max(1, len - 1);
            const extra = Math.max(0, (TARGET_CHARS - len) * BASE_CHAR_EM);
            const spacing = Math.min(MAX_SPACING_EM, extra / gaps);
            return {
                transform: `translateY(calc(-50% + ${scrollY.value * 0.5}px)) scaleY(1.35)`,
                letterSpacing: `${spacing.toFixed(3)}em`,
            };
        });
        const imageStyle = computed(() => ({
            transform: `translateY(${scrollY.value * 0.18}px)`,
        }));
        const taglineStyle = computed(() => ({
            transform: `translateY(${scrollY.value * -0.25}px)`,
        }));
        return {
            heroImage,
            greeting,
            bgTextStyle,
            imageStyle,
            taglineStyle,
        };
    },
});
